import React, { createContext, use, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI, usersAPI, setAuthToken, getAuthToken, setRefreshToken } from '../lib/api';
import { toast } from 'sonner';
import { messageErreur } from '../lib/erreurs';
import { quitterPortee } from '../lib/offline/portee';

const STORAGE_PREFIX = 'app_v1_';
const getStorage = (key) => localStorage.getItem(STORAGE_PREFIX + key) ?? localStorage.getItem(key);
const setStorage = (key, value) => { localStorage.setItem(STORAGE_PREFIX + key, value); };
const removeStorage = (key) => { localStorage.removeItem(STORAGE_PREFIX + key); localStorage.removeItem(key); };

export const AuthContext = createContext(null);

// Un profil sans le réglage « dépôt » (ancien serveur, super administrateur)
// vaut « dépôt géré », le comportement historique.
const avecReglageDepot = (entitlements) =>
  entitlements
    ? { ...entitlements, manages_depot: entitlements.manages_depot !== false }
    : entitlements;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // Attempt to restore user from localStorage (with version prefix fallback)
    try {
      const storedUser = getStorage('user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (e) {
      return null;
    }
  });
  
  const [isLoading, setIsLoading] = useState(true);
  // Dernière relecture du profil: `setUser` relance l'effet de session, sans ce garde-fou
  // le profil était relu en boucle, à chaque nouvel objet utilisateur.
  const profilRelueLe = useRef(0);
  const [error, setError] = useState(null);

  const decodeToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        role: payload.role,
        org_id: payload.org_id,
        user_id: payload.user_id,
        token,
      };
    } catch (e) {
      return null;
    }
  };

  const enrichUserWithProfile = useCallback(async (baseUser) => {
    try {
      const profile = await usersAPI.getMe();
      return {
        ...baseUser,
        ...(profile?.user || {}),
        organization: profile?.organization || null,
        license: profile?.license || null,
        entitlements: avecReglageDepot(profile?.entitlements || null),
      };
    } catch (err) {
      console.warn('Failed to load profile entitlements', err);
      return { ...baseUser, entitlements: avecReglageDepot(baseUser?.entitlements || null) };
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const restoreSession = async () => {
      const storedToken = getAuthToken() || localStorage.getItem('token');
      if (storedToken) {
        setAuthToken(storedToken);
      }

      // Une session déjà ouverte est relue à chaque ouverture de
      // l'application, en arrière-plan: les droits et réglages de la société
      // (dépôt géré ou non, modules) peuvent avoir changé depuis la
      // connexion. Sans cela, une société passée « sans dépôt » gardait la
      // jauge et le vocabulaire du dépôt jusqu'à sa prochaine connexion.
      if (storedToken && user) {
        if (Date.now() - profilRelueLe.current < 60000) {
          if (!cancelled) setIsLoading(false);
          return;
        }
        enrichUserWithProfile(user)
          .then((enriched) => {
            if (!cancelled && enriched) {
              profilRelueLe.current = Date.now();
              setStorage('user', JSON.stringify(enriched));
              setUser(enriched);
            }
          })
          .catch(() => {});
      } else if (storedToken && !user) {
        const decodedUser = decodeToken(storedToken);
        if (decodedUser) {
          const enriched = await enrichUserWithProfile(decodedUser);
          if (!cancelled) {
            setStorage('user', JSON.stringify(enriched));
            setUser(enriched);
          }
        } else {
          setAuthToken(null);
          removeStorage('token');
          removeStorage('user');
        }
      }
      if (!cancelled) setIsLoading(false);
    };

    restoreSession();
    return () => {
      cancelled = true;
    };
  }, [user, enrichUserWithProfile]);

  const loginWithCreds = async (email, password) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await authAPI.loginWithEmail(email, password);
      const access_token = response?.access_token || response?.token;
      if (!access_token) throw new Error('Token absent de la réponse');

      const decodedToken = decodeToken(access_token);
      if (!decodedToken) throw new Error('Token invalide');

      setAuthToken(access_token);
      // Kept so an expired access token can be renewed silently instead of
      // bouncing the user to the login screen every twelve hours.
      setRefreshToken(response?.refresh_token || null);
      const enriched = await enrichUserWithProfile(decodedToken);
      profilRelueLe.current = Date.now();
      setStorage('token', access_token);
      setStorage('user', JSON.stringify(enriched));
      setUser(enriched);

      toast.success('Connexion réussie');
      return { success: true };
    } catch (err) {
      console.error('Login error', err);
      // A request that never reached the server is not a rejected password.
      // Reporting it as "Identifiants invalides" sends the user to re-type a
      // password that was never wrong — say what actually happened instead.
      const errorMsg = messageErreur(err, 'Identifiant ou mot de passe incorrect.');
      setError(errorMsg);
      toast.error(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  };

  // Le login par code a été retiré du web (dashboards = email + mot de passe ;
  // les codes d'accès terrain restent réservés à l'application mobile).

  // Relit le profil après un réglage de la société (dépôt géré ou non).
  const refreshProfile = useCallback(async () => {
    if (!user) return;
    const enriched = await enrichUserWithProfile(user);
    setStorage('user', JSON.stringify(enriched));
    setUser(enriched);
  }, [user, enrichUserWithProfile]);

  const logout = () => {
    quitterPortee();
    setAuthToken(null);
    setRefreshToken(null);
    removeStorage('token');
    removeStorage('user');
    setUser(null);
  };

  // RBAC helpers
  const role = user?.role;
  const isSuperAdmin = role === 'SUPER_ADMIN';
  const isAdminSociete = role === 'ADMIN_SOCIETE';
  const isClientPortal = role === 'CLIENT_PORTAL';
  // TRANSIT was merged into LOGISTIQUE, and the single FINANCE_STATION became
  // three station levels. canAccessTransit is kept as an alias so the ~20 call
  // sites that gate transport screens keep working while meaning "logistics".
  const isStationManager = role === 'GESTIONNAIRE_STATION';
  // Personnel : les sections du menu s'ouvrent selon les modules de son profil
  // (chaque entrée garde ensuite son propre filtre de module).
  const isPersonnel = role === 'PERSONNEL';
  const modulesProfil = isPersonnel ? new Set(user?.entitlements?.enabled_modules || []) : null;
  const profilA = (codes) => Boolean(modulesProfil && codes.some((code) => modulesProfil.has(code)));
  const canAccessStations =
    isSuperAdmin ||
    isAdminSociete ||
    ['GESTIONNAIRE_STATION', 'GERANT_STATION', 'AGENT_STATION'].includes(role) ||
    profilA(['stations', 'tanks', 'pumps', 'prices', 'purchases', 'station_sales', 'station_pos', 'shifts', 'station_expenses', 'station_kpis']);
  const canAccessLogistics =
    isSuperAdmin ||
    isAdminSociete ||
    role === 'LOGISTIQUE' ||
    profilA(['trips', 'trucks', 'drivers', 'orders', 'clients', 'operations', 'gps_tracking', 'invoices', 'stock_movements', 'dispatch_allocations', 'lots']);
  const canAccessTransit = canAccessLogistics;
  const canAccessMaintenance =
    isSuperAdmin || isAdminSociete || role === 'MAINTENANCE' || profilA(['maintenance', 'truck_rentals']);
  const canAccessFuelControl =
    isSuperAdmin ||
    isAdminSociete ||
    role === 'CONTROLEUR_CARBURANT' ||
    profilA(['sites', 'assets', 'fuel_requests', 'fuel_issues', 'fuel_quotas', 'fuel_consumption']);
  // Only the network manager sees the money; a gérant runs one station's day
  // and an agent stands at the till.
  const canAccessFinance =
    isSuperAdmin ||
    isAdminSociete ||
    isStationManager ||
    (profilA(['finance_cockpit']) && user?.entitlements?.can_see_money === true);
  const canManageUsers = isSuperAdmin || isAdminSociete;
  // Prix, coûts et marges: réservés à la direction de la société.
  // Personnel : son profil peut rendre les montants visibles (`/users/me`).
  const canSeeMoney = isSuperAdmin || isAdminSociete || user?.entitlements?.can_see_money === true;
  const isAuthenticated = Boolean(user && (user.token || getAuthToken()));
  const enabledModules =
    user?.entitlements?.enabled_modules ||
    user?.license?.enabled_modules ||
    null;
  const activePack = user?.entitlements?.active_pack || user?.license?.plan || null;
  // Sans dépôt (réglage de la société): pas de jauge ni d'alerte « stock
  // bas »; le stock est le carburant chargé, non livré. Absent = dépôt géré,
  // le comportement historique.
  const managesDepot = user?.entitlements?.manages_depot !== false;
  const hasModule = (moduleCode) => {
    if (!moduleCode || isSuperAdmin) return true;
    if (!enabledModules) return !isPersonnel;
    return enabledModules.includes(moduleCode);
  };

  const canWriteModule = (code) => hasModule(code) && (isSuperAdmin || isAdminSociete || (isPersonnel && (user?.entitlements?.writable_modules || []).includes(code)));

  const value = {
    user,
    isLoading,
    isAuthenticated,
    error,
    loginWithCreds,
    loginWithEmail: loginWithCreds,  // Alias for consistency
    logout,
    refreshProfile,
    // RBAC exports
    isSuperAdmin,
    isAdminSociete,
    isClientPortal,
    canAccessStations,
    canAccessTransit,
    canAccessLogistics,
    canAccessMaintenance,
    canAccessFuelControl,
    canAccessFinance,
    canManageUsers,
    canSeeMoney,
    enabledModules,
    activePack,
    managesDepot,
    hasModule,
    canWriteModule,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = use(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
